import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { SocketContext } from '../../../context/SocketContext';

import { getCalendarDays, getMonthName } from '../helpers/customCalendar';
import { AddIcon, AddUserIcon, UserIcon } from '../../../assets/svg/iconos';
import { closeModal, openModalBottom } from '../../../actions/ui';
import { fetchConToken } from '../../../helpers/fetch';
import useClickOutside from '../../../hooks/useClickOutside';

import { citaLogout, startLoadingCitasMes, setCitas } from '../../../actions/citas';
import { AgendaView } from './AgendaView';
import { ProductosView } from './ProductosView';

//const timezone = process.env.REACT_APP_TIMEZONE;
const usarSockets = process.env.REACT_SOCKETS;

export const CustomCalendar = ({ agendaActive, setAgendaActive, citas, citasState, arrSucursales, arrProfesionales, selectedDate, setSelectedDate, viewValue, setViewValue, setActiveModalReserva, editCita, setEditCita, isLoading, setIsLoading, isRecharging, setIsRecharging, handleNewSale, targetCitaId, setTargetCitaId, isRestoringNavigationState, setIsRestoringNavigationState, previousSelectedDate, setPreviousSelectedDate, history, isReady = false }) => {
    const dispatch = useDispatch();
    const { empresa, profesional } = useSelector(state => state.auth);
    const { colores } = useSelector(state => state.ui);
    const { avisos } = useSelector(state => state.pedidos);

    const [currentMonth, setCurrentMonth] = useState(selectedDate);
    const [monthName, setMonthName] = useState(getMonthName(moment()));
    const [calendarDays, setCalendarDays] = useState([]);
    const [viewType, setViewType] = useState('calendar');
    const [dailyItem, setDailyItem] = useState(null);

    const [selectedDateCalendar, setSelectedDateCalendar] = useState(moment(selectedDate));
    const [activeDnd, setActiveDnd] = useState(false);
    const [visibleDnd, setVisibleDnd] = useState(false);

    const { socket } = useContext(SocketContext);

    const [myTimeOut, setMyTimeOut] = useState(null);

    const [searchTxt, setSearchTxt] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showSearchDropdown, setShowSearchDropdown] = useState(false);
    const [searchHighlight, setSearchHighlight] = useState(-1);
    const searchRef = useRef(null);
    const searchTimerRef = useRef(null);

    useClickOutside(searchRef, useCallback(() => setShowSearchDropdown(false), []));

    const filteredCitas = useMemo(() => {
        if (searchTxt.length < 3) return citas;
        const txt = searchTxt.toLowerCase();
        return citas.filter(cita => {
            const nombre = (cita.usuario?.nombre || '').toLowerCase();
            const apellidos = (cita.usuario?.apellidos || '').toLowerCase();
            const combinado = `${nombre} ${apellidos}`.trim();
            return nombre.includes(txt) || apellidos.includes(txt) || combinado.includes(txt);
        });
    }, [citas, searchTxt]);

    useEffect(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

        if (searchTxt.length < 3) {
            setSearchResults([]);
            setShowSearchDropdown(false);
            return;
        }

        searchTimerRef.current = setTimeout(async () => {
            try {
                const resp = await fetchConToken(
                    `usuarios/buscar/${searchTxt}`,
                    { empresa, from: 1, limit: 5, sort: 'creacion', asc: false },
                    'POST'
                );
                const body = await resp.json();
                if (body.clientes) {
                    setSearchResults(body.clientes);
                } else {
                    setSearchResults([]);
                }
                setShowSearchDropdown(true);
                setSearchHighlight(-1);
            } catch (err) {
                console.error('Error buscando clientes:', err);
                setShowSearchDropdown(false);
            }
        }, 300);

        return () => clearTimeout(searchTimerRef.current);
    }, [searchTxt, empresa]);

    const handleGoToCliente = (e, usuario) => {
        e.stopPropagation();
        setShowSearchDropdown(false);
        setSearchTxt('');
        setSearchResults([]);

        const calendarState = {
            selectedDate: selectedDate ? selectedDate.toISOString() : null,
            viewValue,
            previousSelectedDate: previousSelectedDate ? previousSelectedDate.toISOString() : null
        };

        history?.push({
            pathname: `/cliente/${usuario._id}`,
            state: { usuario, calendarState }
        });
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Escape') {
            setShowSearchDropdown(false);
            setSearchHighlight(-1);
            e.target.blur();
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (showSearchDropdown && searchResults.length > 0) {
                setSearchHighlight(prev => (prev < searchResults.length - 1 ? prev + 1 : 0));
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (showSearchDropdown && searchResults.length > 0) {
                setSearchHighlight(prev => (prev > 0 ? prev - 1 : searchResults.length - 1));
            }
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (searchHighlight >= 0 && searchHighlight < searchResults.length) {
                handleGoToCliente(e, searchResults[searchHighlight]);
            }
        }
    };

    const handleClearSearch = () => {
        setSearchTxt('');
        setSearchResults([]);
        setShowSearchDropdown(false);
    };

    useEffect(() => {
        if (selectedDate) {
            setCurrentMonth(selectedDate);
            setMonthName(getMonthName(selectedDate));
            setCalendarDays(getCalendarDays(selectedDate, filteredCitas, arrSucursales, arrProfesionales));
        } else if (currentMonth) {
            setMonthName(getMonthName(currentMonth));
            setCalendarDays(getCalendarDays(currentMonth, filteredCitas, arrSucursales, arrProfesionales));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredCitas, selectedDate, arrSucursales, arrProfesionales]);

    useEffect(() => {
        if (citas.length === 0) {
            if (!isRecharging) {
                setIsLoading(true);
                setIsRecharging(true);
            }

            setMyTimeOut(
                setTimeout(() => {
                    if (isLoading) {
                        dispatch(openModalBottom({ text: ['No se han encontrado eventos de calendario o ha expirado el tiempo de espera'], timeOut: 4 }));
                    }
                    setIsLoading(false);
                }, 3000)
            )
        } else {
            clearTimeout(myTimeOut);
            setTimeout(null);
        }

        return () => {
            clearTimeout(myTimeOut);
            setTimeout(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [citas]);

    // useEffect(() => {console.log('Cambia selectedDate');}, [selectedDate])
    // useEffect(() => {console.log('Cambia currentMonth');}, [currentMonth])
    // useEffect(() => {console.log('Cambia citas');}, [citas])
    // useEffect(() => {console.log('Cambia arrSucursales');}, [arrSucursales])
    // useEffect(() => {console.log('Cambia arrProfesionales');}, [arrProfesionales])s
    // useEffect(() => {console.log('Cambia isLoading', isLoading );}, [isLoading])

    useEffect(() => {
        if (calendarDays.length > 0) {
            //console.log( calendarDays );
            updateDailyItem();
        }

        return () => {
            clearTimeout(myTimeOut);
            setTimeout(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calendarDays]);

    useEffect(() => {
        if (viewValue === 'day' || viewValue === 'today') {
            setVisibleDnd(true);
        } else {
            setVisibleDnd(false);
        }
    }, [viewValue])

    useEffect(() => {
        if (viewValue === 'today' && calendarDays.length > 0) {
            updateDailyItem();
        }
    }, [viewValue, calendarDays])

    useEffect(() => {
        setSelectedDateCalendar(moment(selectedDate));
    }, [selectedDate])

    const updateDailyItem = (specificDate = null) => {
        const theDate = specificDate || selectedDate || moment();
        calendarDays.forEach(day => {
            if (day.date.format('D-M-YYYY') === theDate.format('D-M-YYYY')) {
                //console.log('updateDailyItem');
                setDailyItem(day);
                dispatch(closeModal());
            }
        });
        if (citas.length > 0) {
            setIsLoading(false);
        }
    }

    const changeDay = (operation) => {
        let newDate;
        let newMonth;
        if (operation === 'add') {
            newDate = moment(selectedDate).add(1, 'days');
            newMonth = moment(currentMonth).add(1, 'days');
        } else {
            newDate = moment(selectedDate).subtract(1, 'days');
            newMonth = moment(currentMonth).subtract(1, 'days');
        }
        if (currentMonth.format('MM') !== newMonth.format('MM')) {
            const fechaString = newDate.toISOString();
            const monthKey = fechaString.substring(0, 7); // "2025-09"
                        
            if (!citasState.citasByMonth || !citasState.citasByMonth[monthKey]) {
                // dispatch(startLoadingCitasMes(empresa, fechaString));
            }
        } else {
            const fechaString = newDate.toISOString();
            const monthKey = fechaString.substring(0, 7); // "YYYY-MM"
            
            if (!citasState.citasByMonth || !citasState.citasByMonth[monthKey]) {
                if (usarSockets) {
                    socket?.emit('obtener-citas-mes', {
                        empresa,
                        fecha: fechaString,
                        profesionalId: profesional?._id
                    });
                } else {
                    // dispatch(startLoadingCitasMes(empresa, fechaString, profesional?._id));
                }
            }
        }
        setSelectedDate(newDate);
        setSelectedDateCalendar(newDate);
        setCurrentMonth(newMonth);
    };

    const changeMonth = (operation) => {
        dispatch(citaLogout());
        let newDate;
        if (operation === 'add') {
            newDate = moment(currentMonth).add(1, 'months');
        } else {
            newDate = moment(currentMonth).subtract(1, 'months');
        }
        const fechaString = newDate.toISOString();
        const monthKey = fechaString.substring(0, 7); // "2025-09"
        
        if (!citasState.citasByMonth || !citasState.citasByMonth[monthKey]) {
            if (usarSockets) {
                socket?.emit('obtener-citas-mes', {
                    empresa,
                    fecha: fechaString,
                    profesionalId: profesional?._id
                });
            } else {
                setIsRecharging(false);
                // dispatch(startLoadingCitasMes(empresa, fechaString, profesional?._id));
            }
        }
        setSelectedDate(newDate);
        setCurrentMonth(newDate);
    };

    const selectDate = (date) => {
        setSelectedDate(date.date);
        setSelectedDateCalendar(date.date);
        setCurrentMonth(date.date);
        changeView('day');
        setDailyItem(date);
    }

    const restoreCitasForDate = (date) => {
        const fechaString = date.toISOString();
        const monthKey = fechaString.substring(0, 7);
        
        if (citasState.citasByMonth && citasState.citasByMonth[monthKey]) {
            dispatch(setCitas({ 
                citas: citasState.citasByMonth[monthKey], 
                fecha: fechaString 
            }));
        } else {
            if (usarSockets) {
                socket?.emit('obtener-citas-mes', {
                    empresa,
                    fecha: fechaString,
                    profesionalId: profesional?._id
                });
            } else {
                dispatch(startLoadingCitasMes(empresa, fechaString, profesional?._id));
            }
        }
    };

    const changeView = (selectedItem) => {
        setViewValue(selectedItem);
        
        if (selectedItem === 'month') {
            if (viewValue === 'today' && previousSelectedDate) {
                setSelectedDate(previousSelectedDate);
                setSelectedDateCalendar(previousSelectedDate);
                setCurrentMonth(previousSelectedDate);
                setPreviousSelectedDate(null);
                
                restoreCitasForDate(previousSelectedDate);
            } else if (!selectedDate) {
                setSelectedDate(moment());
            }
            if (window.history && window.history.replaceState) {
                window.history.replaceState({}, document.title, window.location.pathname);
            }
        } else if (selectedItem === 'today') {
            if (selectedDate && viewValue !== 'today') {
                setPreviousSelectedDate(selectedDate);
            }
            
            const today = moment();
            setSelectedDate(today);
            setSelectedDateCalendar(today);
            setCurrentMonth(today);
            setTargetCitaId(null);
            setIsRestoringNavigationState(false);
            
            setTimeout(() => {
                updateDailyItem(today);
            }, 100);
            
            if (window.history && window.history.replaceState) {
                window.history.replaceState({}, document.title, window.location.pathname);
            }
        } else if (selectedItem === 'day') {
            if (viewValue === 'today' && previousSelectedDate) {
                setSelectedDate(previousSelectedDate);
                setSelectedDateCalendar(previousSelectedDate);
                setCurrentMonth(previousSelectedDate);
                setPreviousSelectedDate(null);
                
                restoreCitasForDate(previousSelectedDate);
            }
        }
    };

    const changeType = (selectedItem) => {
        setViewType(selectedItem);
    };

    const clickAdd = () => {
        setEditCita(false);
        setActiveModalReserva(true);
    }

    const clickAddCliente = () => {
        const calendarState = {
            selectedDate: selectedDate ? selectedDate.toISOString() : null,
            viewValue,
            previousSelectedDate: previousSelectedDate ? previousSelectedDate.toISOString() : null
        };
        history?.push({
            pathname: '/cliente/new',
            state: { activeTab: 'ultima', calendarState }
        });
    }

    const toggleDnd = () => {
        setActiveDnd(!activeDnd);
    }

    return (
        <div className={`ylm-calendar ${agendaActive ? '' : 'big'}`}>
            <div className="calendar-preheader">
                <div className="title">
                    <h1 style={{ color: colores.colorPrincipal }}>Reservas</h1>
                </div>
                <div className="selector">
                    <p
                        className={`${agendaActive ? 'active' : ''}`}
                        style={{
                            color: agendaActive ? colores.colorPrincipal : colores.colorSecundario,
                            backgroundColor: agendaActive ? colores.colorSecundario : 'transparent'
                        }}
                        onClick={() => setAgendaActive(true)}
                    >Agenda</p>
                    <p
                        className={`${!agendaActive ? 'active' : ''}`}
                        style={{
                            color: agendaActive ? colores.colorSecundario : colores.colorPrincipal,
                            backgroundColor: !agendaActive ? colores.colorSecundario : 'transparent'
                        }}
                        onClick={() => setAgendaActive(false)}
                    >
                        Productos
                        {
                            avisos > 0 &&
                            <span className="badge" style={{ backgroundColor: colores.colorPrincipal }}>{avisos}</span>
                        }

                    </p>
                </div>
                <div className="botonera">
                    <div className="search-clientes" ref={searchRef}>
                        <div className="search-input-wrapper">
                            <input
                                type="text"
                                placeholder="Buscar cliente..."
                                value={searchTxt}
                                onChange={(e) => setSearchTxt(e.target.value)}
                                onKeyDown={handleSearchKeyDown}
                                style={{
                                    color: colores.colorPrincipal,
                                    borderColor: colores.colorSecundario
                                }}
                            />
                            {
                                searchTxt.length > 0 &&
                                <span className="search-clear" onClick={handleClearSearch}>&times;</span>
                            }
                        </div>
                        {
                            showSearchDropdown && searchResults.length > 0 &&
                            <div className="search-dropdown">
                                {
                                    searchResults.map((usuario, idx) => (
                                        <div
                                            key={usuario._id}
                                            className={`search-item ${idx === searchHighlight ? 'highlighted' : ''}`}
                                            onClick={(e) => handleGoToCliente(e, usuario)}
                                        >
                                            <div className="search-info">
                                                <span className="search-name">{usuario.nombre} {usuario.apellidos || ''}</span>
                                                { usuario.telefono && <span className="search-phone">{usuario.telefono}</span> }
                                            </div>
                                            <div className="search-go-ficha">
                                                <UserIcon color={colores.colorPrincipal} width="14" height="14" />
                                            </div>
                                        </div>
                                    ))
                                }
                            </div>
                        }
                        {
                            showSearchDropdown && searchResults.length === 0 && searchTxt.length >= 3 &&
                            <div className="search-dropdown">
                                <div className="search-item no-results">Sin resultados</div>
                            </div>
                        }
                    </div>
                    <div
                        className={`btn-add${!isReady ? ' disabled' : ''}`}
                        onClick={isReady ? clickAddCliente : undefined}
                        title="Nuevo cliente"
                    >
                        <AddUserIcon
                            color={isReady ? colores.colorPrincipal : '#ccc'}
                        />
                    </div>
                    <div
                        className={`btn-add${!isReady ? ' disabled' : ''}`}
                        onClick={isReady ? clickAdd : undefined}
                        title="Nueva cita"
                    >
                        <AddIcon
                            color={isReady ? colores.colorPrincipal : '#ccc'}
                        />
                    </div>
					<div
                        className={`btn-add${!isReady ? ' disabled' : ''}`}
                        onClick={isReady ? handleNewSale : undefined}
						style={{
							backgroundColor: '#FFFBD6',
							padding: '10px'
						}}
                        title="Nueva venta"
                    >
                        <AddIcon
                            color={colores.colorPrincipal}
						
                        />
                    </div>
					
                </div>
            </div>
            {
                agendaActive ?
                    <AgendaView
                        viewType={viewType}
                        viewValue={viewValue}
                        changeMonth={changeMonth}
                        monthName={monthName}
                        currentMonth={currentMonth}
                        changeDay={changeDay}
                        selectedDateCalendar={selectedDateCalendar}
                        changeView={changeView}
                        changeType={changeType}
                        calendarDays={calendarDays}
                        selectedDate={selectedDate}
                        selectDate={selectDate}
                        arrSucursales={arrSucursales}
                        arrProfesionales={arrProfesionales}
                        dailyItem={dailyItem}
                        editCita={editCita}
                        setEditCita={setEditCita}
                        activeDnd={activeDnd}
                        setActiveDnd={setActiveDnd}
                        citas={filteredCitas}
                        setActiveModalReserva={setActiveModalReserva}
                        visibleDnd={visibleDnd}
                        toggleDnd={toggleDnd}
                        targetCitaId={targetCitaId}
                        setTargetCitaId={setTargetCitaId}
                    />
                    :
                    <ProductosView />
            }
        </div>
    )
}
